import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiResponse } from 'src/app/models/api-response.model';
import { ProviderLaneEntry, ProviderRosterEntry } from 'src/app/models/provider-roster.model';
import { environment } from 'src/environments/environment';
import { ProviderSummaryTime } from './models/provider.model';

@Injectable({
  providedIn: 'root',
})
export class ProviderService {
  baseUrl = `${environment.welbyEndpoint}/api/v1/core/providers`;
  constructor(private http: HttpClient) {}

  getSummaryTime(providerId: string): Observable<ProviderSummaryTime> {
    return this.http.get<ProviderSummaryTime>(`${this.baseUrl}/${providerId}/summary-time`);
  }

  /**
   * Provider roster of one client, with each account's routing LANE resolved server-side by
   * `ProviderLaneService.laneOf()` (welby-micro-service, BT S-03 #1228).
   *
   * The single read behind two consumers — Bulk Transfer's selectors and the Care Call Guide's
   * escalation target (CHF-E4 §0d) — so the frontend never derives a lane of its own. Deriving it
   * from `providers[].role` (the licensure string) is what S-03 measured and rejected: licensure
   * appears in no visibility query, 10 of 49 panel-holding providers on production carry no value
   * for it, and the two predicates disagree on real accounts.
   *
   * The endpoint uses the standardized response envelope, so `.data` is unwrapped here rather than
   * in the caller (LEDGER 5.7). Providers whose lane could not be determined are already excluded
   * server-side. Errors are NOT swallowed here — each caller decides what an unavailable roster
   * means for it (Bulk Transfer surfaces it; the guide fails closed onto "no NCM found").
   */
  getRosterByClient(clientId: string): Observable<ProviderRosterEntry[]> {
    return this.http
      .get<ApiResponse<ProviderRosterEntry[]>>(`${this.baseUrl}/by-client/${encodeURIComponent(clientId)}`)
      .pipe(map((response) => response.data ?? []));
  }

  /**
   * Routing lanes of specific accounts — `{ uid, lane }` and nothing else.
   *
   * The read behind the Care Call Guide's escalation target (CHF-E4 §0d). Deliberately NOT
   * `getRosterByClient`: that route is `@Roles('isAdmin')` by intent and this caller is a medical
   * assistant, so pointing the care call at it made every guide's escalation task depend on the
   * backend's `RolesGuard` staying in shadow mode. It also ships names, emails, licensure and a
   * per-provider panel count to a screen that renders none of them. See `ProviderLaneEntry`.
   *
   * `uids` IS REQUIRED, and passing the patient's own `providers[]` uids is the whole contract.
   * Omitting it makes the endpoint answer with the lanes of the client's MEMBERS — the population
   * `client_account_to_user` holds — and that is not the population an escalation target lives in.
   * Measured on dev 2026-09-04 over the 795 patients holding a provider uid: 181 of the 758 who have
   * a lane-'rn' provider have that provider carrying no membership row on their client, so the
   * membership answer resolves NOBODY and the escalation reaches no one (24 %). The gap in that
   * collection is inherited (welby-micro-service #934); what is fixed here is depending on it.
   *
   * `clientId` therefore no longer selects the population — it is the tenant boundary the backend's
   * `ClientMembershipGuard` checks the CALLER against, and it stays in the path for that reason.
   *
   * Same envelope, same `.data` unwrap here rather than in the caller (LEDGER 5.7). Errors are not
   * swallowed: the guide fails closed onto "no NCM found" and says so, which is its decision to make.
   */
  getLanesByClient(clientId: string, uids: string[]): Observable<ProviderLaneEntry[]> {
    // `HttpParams` rather than string concatenation: a uid is a Firestore id today, but the day one
    // is not, an unencoded `&` in the query silently truncates the list and the guide quietly stops
    // resolving assignees. Sent even when empty, so the backend never falls back to the member map.
    const params = new HttpParams().set('uids', uids.join(','));
    return this.http
      .get<ApiResponse<ProviderLaneEntry[]>>(`${this.baseUrl}/lanes-by-client/${encodeURIComponent(clientId)}`, { params })
      .pipe(map((response) => response.data ?? []));
  }
}
